
import React, { useState, useEffect } from 'react';
import { Lesson, CheckInQuestion } from '@/data/lessons';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { 
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage 
} from "@/components/ui/form";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";

interface LessonEditorProps {
  lesson: Lesson;
  onSave: (updatedLesson: Lesson) => void;
}

// Fixed form schema to require type and question fields
const formSchema = z.object({
  day: z.number().min(1).max(30),
  title: z.string().min(2, { message: "Title must be at least 2 characters." }),
  subtitle: z.string(),
  videoId: z.string(),
  content: z.array(z.string()),
  actionStep: z.object({
    title: z.string().min(2, { message: "Action step title must be at least 2 characters." }),
    description: z.string()
  }),
  checkInQuestion: z.object({
    type: z.enum(["multiple-choice", "text", "mood"]),
    question: z.string().min(1, { message: "Question is required." }),
    options: z.array(z.string()).optional()
  }),
  quote: z.string().optional(),
  quoteAuthor: z.string().optional(),
  points: z.number().min(1)
});

type FormValues = z.infer<typeof formSchema>;

const LessonEditor: React.FC<LessonEditorProps> = ({ lesson, onSave }) => {
  const [contentFields, setContentFields] = useState<string[]>(lesson.content);
  const [questionOptions, setQuestionOptions] = useState<string[]>(
    lesson.checkInQuestion.options || []
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...lesson,
      checkInQuestion: {
        type: lesson.checkInQuestion.type,
        question: lesson.checkInQuestion.question,
        options: lesson.checkInQuestion.options || []
      }
    }
  });

  // Update form when lesson changes
  useEffect(() => {
    form.reset({
      ...lesson,
      checkInQuestion: {
        type: lesson.checkInQuestion.type,
        question: lesson.checkInQuestion.question,
        options: lesson.checkInQuestion.options || []
      }
    });
    setContentFields(lesson.content);
    setQuestionOptions(lesson.checkInQuestion.options || []);
  }, [lesson, form]);

  const handleAddContentField = () => {
    setContentFields([...contentFields, ""]);
  };

  const handleRemoveContentField = (index: number) => {
    const newFields = [...contentFields];
    newFields.splice(index, 1);
    setContentFields(newFields);
    form.setValue("content", newFields);
  };

  const handleContentChange = (index: number, value: string) => {
    const newFields = [...contentFields];
    newFields[index] = value;
    setContentFields(newFields);
    form.setValue("content", newFields);
  };

  const handleAddOption = () => {
    setQuestionOptions([...questionOptions, ""]);
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = [...questionOptions];
    newOptions.splice(index, 1);
    setQuestionOptions(newOptions);
    form.setValue("checkInQuestion.options", newOptions);
  };

  const handleOptionChange = (index: number, value: string) => {
    const newOptions = [...questionOptions];
    newOptions[index] = value;
    setQuestionOptions(newOptions);
    form.setValue("checkInQuestion.options", newOptions);
  };

  const onSubmit = (data: FormValues) => {
    // Make sure we're creating a valid CheckInQuestion object
    const checkInQuestion: CheckInQuestion = {
      type: data.checkInQuestion.type,
      question: data.checkInQuestion.question,
      options: data.checkInQuestion.type === "multiple-choice" ? questionOptions : undefined
    };

    // Explicitly create a Lesson object with all required fields
    const updatedLesson: Lesson = {
      day: data.day,
      title: data.title,
      subtitle: data.subtitle,
      videoId: data.videoId,
      content: contentFields,
      actionStep: {
        title: data.actionStep.title,
        description: data.actionStep.description
      },
      checkInQuestion: checkInQuestion,
      points: data.points,
      quote: data.quote,
      quoteAuthor: data.quoteAuthor
    };
    
    onSave(updatedLesson);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="day"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Day</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="points"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Points</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={1}
                    {...field}
                    onChange={e => field.onChange(parseInt(e.target.value))}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Title</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="subtitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Subtitle</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="videoId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>YouTube Video ID</FormLabel>
              <FormControl>
                <Input {...field} placeholder="e.g. dQw4w9WgXcQ" />
              </FormControl>
              <FormDescription>
                This is the ID after "v=" in YouTube URLs (e.g., youtube.com/watch?v=dQw4w9WgXcQ)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="space-y-4">
          <Label>Content Paragraphs</Label>
          {contentFields.map((content, index) => (
            <div key={index} className="flex gap-2">
              <Textarea
                value={content}
                onChange={(e) => handleContentChange(index, e.target.value)}
                placeholder={`Paragraph ${index + 1}`}
                className="flex-1"
              />
              <Button
                type="button"
                variant="destructive"
                size="icon"
                onClick={() => handleRemoveContentField(index)}
              >
                X
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            onClick={handleAddContentField}
          >
            Add Paragraph
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="actionStep.title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Step Title</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="actionStep.description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Action Step Description</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <div className="space-y-4">
          <FormField
            control={form.control}
            name="checkInQuestion.type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Check-in Question Type</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a question type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="multiple-choice">Multiple Choice</SelectItem>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="mood">Mood</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="checkInQuestion.question"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Check-in Question</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          {form.watch("checkInQuestion.type") === "multiple-choice" && (
            <div className="space-y-2">
              <Label>Question Options</Label>
              {questionOptions.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => handleOptionChange(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    onClick={() => handleRemoveOption(index)}
                  >
                    X
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                onClick={handleAddOption}
              >
                Add Option
              </Button>
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            control={form.control}
            name="quote"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quote (Optional)</FormLabel>
                <FormControl>
                  <Textarea {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="quoteAuthor"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Quote Author (Optional)</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        
        <Button type="submit" className="w-full">Save Lesson</Button>
      </form>
    </Form>
  );
};

export default LessonEditor;
